/**
 * Catálogo ÚNICO de nomes de slot.
 *
 * Existiam dois: `ActionSlot` (em `plugin.ts`, lado declarativo) e os objetos
 * `domains/*.slots` (lado imperativo, `registerSlot`). Eles divergiram — os
 * slots de conexão (`connection-status`, `connection-actions`) e os de mensagem
 * (`message-content`, `message-footer`, ...) só existem nos domínios; nunca
 * chegaram ao `ActionSlot`. Enquanto os dois mecanismos eram separados isso
 * passava; com o PluginBridge montando os dois motores em todo ponto, qualquer
 * slot aceita as duas formas e um catálogo só é obrigatório.
 *
 * A fonte da verdade são os domínios (que já são `as const`); aqui só derivamos
 * a união de tipos e a lista de runtime usada nos diagnósticos.
 */
import { app } from "./domains/app";
import { connections } from "./domains/connections";
import { contacts } from "./domains/contacts";
import { plugins } from "./domains/plugins";
import { tickets } from "./domains/tickets";
import { describeShape, type Shape, type SlotDefinition, type SlotRegistry } from "./slotSchema";

type SlotValues<T> = T[keyof T];

/**
 * Slots que nasceram no `ActionSlot` e nunca ganharam domínio. Ficam aqui para
 * não quebrar quem já os declara — não crie novos por este caminho.
 */
const LEGACY_SLOTS = ["dashboard-widgets", "topbar-actions"] as const;

/** Nome válido de slot — vale para `provides.actions` e para `registerSlot`. */
export type SlotName =
  | SlotValues<typeof tickets.slots>
  | SlotValues<typeof contacts.slots>
  | SlotValues<typeof connections.slots>
  | SlotValues<typeof plugins.slots>
  | SlotValues<typeof app.slots>
  | (typeof LEGACY_SLOTS)[number];

/** Todos os slots do catálogo (runtime) — usado para acusar nome inexistente. */
export const KNOWN_SLOTS: readonly SlotName[] = Array.from(
  new Set<SlotName>([
    ...(Object.values(tickets.slots) as SlotName[]),
    ...(Object.values(contacts.slots) as SlotName[]),
    ...(Object.values(connections.slots) as SlotName[]),
    ...(Object.values(plugins.slots) as SlotName[]),
    ...(Object.values(app.slots) as SlotName[]),
    ...(LEGACY_SLOTS as readonly SlotName[]),
  ])
);

/**
 * Slots que continuam no tipo mas que NENHUM ponto do app renderiza. Declarar
 * ação ou controlador neles não produz efeito algum — por isso o aviso.
 */
export const DEPRECATED_SLOTS: readonly SlotName[] = ["topbar-actions"];

export function isKnownSlot(name: string): name is SlotName {
  return (KNOWN_SLOTS as readonly string[]).indexOf(name) >= 0;
}

export function isDeprecatedSlot(name: string): boolean {
  return (DEPRECATED_SLOTS as readonly string[]).indexOf(name) >= 0;
}

/** Uma entrada do registro plano: de que domínio veio e o que declara. */
export interface SlotRegistryEntry extends SlotDefinition<string, Shape> {
  domain: "tickets" | "contacts" | "connections" | "plugins" | "app";
  /** A chave no domínio (`tickets.slots.ACTIONS` → "ACTIONS"). */
  key: string;
}

/**
 * Registro PLANO nome → definição (descrição + contrato), juntando os cinco
 * domínios. É o que a tooling (`sdk-info.mjs slots|context`) e o frontend leem
 * para saber o que cada slot recebe — sem cópia mantida à mão.
 */
export const SLOT_REGISTRY: Readonly<Record<string, SlotRegistryEntry>> = (() => {
  const out: Record<string, SlotRegistryEntry> = {};
  const add = (domain: SlotRegistryEntry["domain"], registry: SlotRegistry) => {
    for (const [key, def] of Object.entries(registry)) out[def.name] = { ...def, domain, key };
  };
  add("tickets", tickets.registry);
  add("contacts", contacts.registry);
  add("connections", connections.registry);
  add("plugins", plugins.registry);
  add("app", app.registry);
  return out;
})();

/** `ticketId: number, queue?: { id: number, name: string, color?: string }` — ou `null` se o slot não existe. */
export function describeSlotContext(name: string): string | null {
  const entry = SLOT_REGISTRY[name];
  return entry ? describeShape(entry.context) : null;
}
